/**
 * Session 管理工具
 *
 * 提供 Session 不活動超時管理：
 * - 追蹤使用者活動
 * - 不活動超時自動登出
 * - 超時前警告通知
 * - Session 生命週期管理
 */

import { logout } from '@/store/auth-store';
import { createLogger } from './logger';

const logger = createLogger('SessionManager');

// ========================================================================
// Session Manager 類別
// ========================================================================

export interface SessionManagerOptions {
  /** 不活動超時時間（毫秒），預設 30 分鐘 */
  inactivityTimeout?: number;
  /** 警告通知的提前時間（毫秒），預設 5 分鐘 */
  warningTime?: number;
  /** 超時前警告回呼 */
  onWarning?: () => void;
  /** 超時後回呼（在登出前觸發） */
  onTimeout?: () => void;
}

export class SessionManager {
  private lastActivityTime: number = Date.now();
  private inactivityTimeout: number;
  private warningTime: number;
  private inactivityTimer: NodeJS.Timeout | null = null;
  private warningTimer: NodeJS.Timeout | null = null;
  private onWarning?: () => void;
  private onTimeout?: () => void;
  private isActive: boolean = false;
  private trackedEvents: string[] = [];

  constructor(options: SessionManagerOptions = {}) {
    this.inactivityTimeout = options.inactivityTimeout ?? 30 * 60 * 1000; // 30 分鐘
    this.warningTime = options.warningTime ?? 5 * 60 * 1000; // 5 分鐘
    this.onWarning = options.onWarning;
    this.onTimeout = options.onTimeout;

    // 確保警告時間小於超時時間
    if (this.warningTime >= this.inactivityTimeout) {
      this.warningTime = Math.max(1000, this.inactivityTimeout - 60000); // 至少提前 1 分鐘
      logger.warn('Warning time adjusted to be less than inactivity timeout', {
        warningTime: this.warningTime,
        inactivityTimeout: this.inactivityTimeout
      });
    }
  }

  /**
   * 啟動 Session 監控
   */
  start(): void {
    if (this.isActive) {
      logger.warn('Session manager is already active');
      return;
    }

    this.isActive = true;
    this.resetActivity();
    this.bindEvents();

    logger.info('Session manager started', {
      inactivityTimeoutMinutes: this.inactivityTimeout / 60000,
      warningTimeMinutes: this.warningTime / 60000
    });
  }

  /**
   * 停止 Session 監控
   */
  stop(): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.clearTimers();
    this.unbindEvents();

    logger.info('Session manager stopped');
  }

  /**
   * 重置活動時間
   */
  resetActivity(): void {
    if (!this.isActive) return;

    this.lastActivityTime = Date.now();
    this.clearTimers();

    // 設定警告計時器（在超時前觸發）
    const warningDelay = this.inactivityTimeout - this.warningTime;
    if (warningDelay > 0) {
      this.warningTimer = setTimeout(() => {
        this.handleWarning();
      }, warningDelay);
    }

    // 設定不活動超時計時器
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivityTimeout();
    }, this.inactivityTimeout);
  }

  /**
   * 取得距離上次活動的時間（毫秒）
   */
  getTimeSinceLastActivity(): number {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * 取得距離超時的剩餘時間（毫秒）
   */
  getTimeUntilTimeout(): number {
    const elapsed = this.getTimeSinceLastActivity();
    return Math.max(0, this.inactivityTimeout - elapsed);
  }

  /**
   * 格式化剩餘時間為可讀字串
   */
  formatTimeUntilTimeout(): string {
    const remaining = this.getTimeUntilTimeout();
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes} 分 ${seconds} 秒`;
    }
    return `${seconds} 秒`;
  }

  /**
   * 手動觸發超時（用於測試）
   */
  triggerTimeout(): void {
    logger.info('Manually triggering timeout');
    this.handleInactivityTimeout();
  }

  /**
   * 銷毀 Session Manager
   */
  destroy(): void {
    this.stop();
    logger.info('Session manager destroyed');
  }

  // ========================================================================
  // 私有方法
  // ========================================================================

  /**
   * 綁定活動事件
   */
  private bindEvents(): void {
    // 追蹤的活動事件
    this.trackedEvents = [
      'mousemove',
      'mousedown',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'focus',
    ];

    // 為每個事件綁定處理器（使用 passive 提升性能）
    this.trackedEvents.forEach(event => {
      document.addEventListener(event, this.handleActivity, { passive: true });
    });

    logger.debug('Activity events bound', { events: this.trackedEvents });
  }

  /**
   * 解綁活動事件
   */
  private unbindEvents(): void {
    this.trackedEvents.forEach(event => {
      document.removeEventListener(event, this.handleActivity);
    });

    logger.debug('Activity events unbound');
  }

  /**
   * 處理活動事件（箭頭函數確保 this 綁定正確）
   */
  private handleActivity = (): void => {
    this.resetActivity();
  };

  /**
   * 清除所有計時器
   */
  private clearTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * 處理警告通知
   */
  private handleWarning(): void {
    const remaining = this.formatTimeUntilTimeout();
    logger.warn('Session will expire soon', { remaining });

    // 觸發警告回呼
    try {
      this.onWarning?.();
    } catch (error) {
      logger.error('Error in warning callback:', error);
    }
  }

  /**
   * 處理不活動超時
   */
  private handleInactivityTimeout(): void {
    logger.warn('Session expired due to inactivity');

    // 觸發超時回呼
    try {
      this.onTimeout?.();
    } catch (error) {
      logger.error('Error in timeout callback:', error);
    }

    // 停止監控
    this.stop();

    // 自動登出
    logout();

    // 重新載入頁面（清除應用狀態）
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }
}

// ========================================================================
// 工具函數
// ========================================================================

/**
 * 建立預設的 Session Manager 實例
 * @param options Session Manager 選項
 * @returns Session Manager 實例
 */
export function createSessionManager(options?: SessionManagerOptions): SessionManager {
  return new SessionManager(options);
}
